import { useEffect, useMemo, useState, type ReactNode, type FormEvent } from 'react';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Can } from '../../shell/Can.js';
import {
  listRoles,
  listPermissions,
  getPermissionMatrix,
  assignPermission,
  revokePermission,
  type Role,
  type Permission,
  type RolePermissionMap,
} from '../../api/rolesApi.js';
import {
  listFieldRules,
  upsertFieldRule,
  deactivateFieldRule,
  FIELD_VISIBILITIES,
  FIELD_VISIBILITY_LABELS,
  type FieldSecurityRule,
  type FieldVisibility,
} from '../../api/fieldSecurityApi.js';

/**
 * PermissionMatrixPage — Permission Master (M02 §9/§11/§12).
 *
 * Two granularity layers, both wired to the real IAM backend:
 *   • Screen security (§11): role × action grants on a resource/screen, using
 *     the T016 permission catalog + T017 role_permission_map endpoints.
 *   • Field-level security (§12): per role+screen+field visibility rules
 *     (Hidden / Read only / Editable / Mandatory) via the T076 field-security
 *     endpoints.
 *
 * The eight §11 screen actions every screen must support, role-wise.
 */
const SCREEN_ACTIONS = [
  { code: 'VIEW', labelKey: 'roles.matrix.action.view' },
  { code: 'CREATE', labelKey: 'roles.matrix.action.create' },
  { code: 'EDIT', labelKey: 'roles.matrix.action.edit' },
  { code: 'DELETE', labelKey: 'roles.matrix.action.delete' },
  { code: 'APPROVE', labelKey: 'roles.matrix.action.approve' },
  { code: 'REJECT', labelKey: 'roles.matrix.action.reject' },
  { code: 'EXPORT', labelKey: 'roles.matrix.action.export' },
  { code: 'PRINT', labelKey: 'roles.matrix.action.print' },
] as const;

/** Convenience presets for the resource selector (admin may type any code). */
const RESOURCE_PRESETS = ['USERS', 'ROLES', 'MENUS', 'COMPANIES', 'BRANDING', 'THEMES'];

/** A checkbox that is interactive only when the caller may edit permissions. */
function GatedCheckbox({
  ariaLabel,
  checked,
  onToggle,
}: {
  readonly ariaLabel: string;
  readonly checked: boolean;
  readonly onToggle: (next: boolean) => void;
}): ReactNode {
  return (
    <Can
      path="/admin/roles"
      action="edit"
      fallback={<input type="checkbox" aria-label={ariaLabel} checked={checked} disabled readOnly />}
    >
      <input
        type="checkbox"
        aria-label={ariaLabel}
        checked={checked}
        onChange={(e) => onToggle(e.target.checked)}
      />
    </Can>
  );
}

export function PermissionMatrixPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();

  const [roles, setRoles] = useState<readonly Role[]>([]);
  const [permissions, setPermissions] = useState<readonly Permission[]>([]);
  // Active permission grants keyed by roleId.
  const [grants, setGrants] = useState<Record<string, RolePermissionMap[]>>({});
  const [fieldRules, setFieldRules] = useState<readonly FieldSecurityRule[]>([]);

  const [resource, setResource] = useState('USERS');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Field-rule form state.
  const [frRole, setFrRole] = useState('');
  const [frScreen, setFrScreen] = useState('');
  const [frField, setFrField] = useState('');
  const [frVis, setFrVis] = useState<FieldVisibility>('READ_ONLY');
  const [frNote, setFrNote] = useState('');
  const [frError, setFrError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [roleList, permList, rules, matrix] = await Promise.all([
          listRoles(token),
          listPermissions(token),
          listFieldRules({}, token),
          getPermissionMatrix(token),
        ]);
        if (!alive) return;
        setRoles(roleList);
        setPermissions(permList);
        setGrants(Object.fromEntries(matrix.map((m) => [m.roleId, [...m.permissions]])));
        setFieldRules(rules);
        if (roleList.length > 0) setFrRole(roleList[0].roleCode);
      } catch (e) {
        if (alive) setError((e as Error).message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  /** permissionCode → permissionId from the catalog. */
  const permIdByCode = useMemo(() => {
    const m: Record<string, string> = {};
    for (const p of permissions) m[p.permissionCode] = p.permissionId;
    return m;
  }, [permissions]);

  /** The active grant (if any) for a role+action on the selected resource. */
  function grantFor(roleId: string, actionCode: string): RolePermissionMap | undefined {
    const permId = permIdByCode[actionCode];
    if (!permId) return undefined;
    return (grants[roleId] ?? []).find(
      (g) => g.permissionId === permId && g.resourceType === resource,
    );
  }

  async function toggleAction(role: Role, actionCode: string, next: boolean) {
    const permId = permIdByCode[actionCode];
    if (!permId) return;
    setError(null);
    setNotice(null);
    try {
      if (next) {
        const map = await assignPermission(role.roleId, permId, resource, token);
        setGrants((prev) => ({
          ...prev,
          [role.roleId]: [...(prev[role.roleId] ?? []), map],
        }));
        setNotice(t('roles.matrix.granted', { action: actionCode, resource, role: role.roleName }));
      } else {
        const existing = grantFor(role.roleId, actionCode);
        if (!existing) return;
        await revokePermission(role.roleId, existing.mapId, token);
        setGrants((prev) => ({
          ...prev,
          [role.roleId]: (prev[role.roleId] ?? []).filter((g) => g.mapId !== existing.mapId),
        }));
        setNotice(t('roles.matrix.revoked', { action: actionCode, resource, role: role.roleName }));
      }
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function addFieldRule(e: FormEvent) {
    e.preventDefault();
    setFrError(null);
    setNotice(null);
    if (!frRole.trim() || !frScreen.trim() || !frField.trim()) {
      setFrError(t('roles.matrix.fr.required'));
      return;
    }
    try {
      const rule = await upsertFieldRule(
        {
          roleCode: frRole.trim(),
          screenCode: frScreen.trim(),
          fieldName: frField.trim(),
          visibility: frVis,
          ruleNote: frNote.trim() || undefined,
        },
        token,
      );
      setFieldRules((prev) => {
        const rest = prev.filter(
          (r) =>
            !(
              r.roleCode === rule.roleCode &&
              r.screenCode === rule.screenCode &&
              r.fieldName === rule.fieldName
            ),
        );
        return [...rest, rule];
      });
      setFrScreen('');
      setFrField('');
      setFrNote('');
      setNotice(t('roles.matrix.fr.saved', { field: rule.fieldName }));
    } catch (err) {
      setFrError((err as Error).message);
    }
  }

  async function removeFieldRule(rule: FieldSecurityRule) {
    setError(null);
    setNotice(null);
    try {
      await deactivateFieldRule(rule.ruleId, token);
      setFieldRules((prev) => prev.filter((r) => r.ruleId !== rule.ruleId));
      setNotice(t('roles.matrix.fr.deactivated', { field: rule.fieldName }));
    } catch (e) {
      setError((e as Error).message);
    }
  }

  if (loading) return <p>{t('roles.matrix.loading')}</p>;
  if (error && roles.length === 0) return <p role="alert">{error}</p>;

  return (
    <main aria-label="permission-matrix">
      <h1>{t('roles.matrix.title')}</h1>
      {error ? <p role="alert">{error}</p> : null}
      {notice ? <p role="status">{notice}</p> : null}

      {/* ── §11 Screen security (action-level, role-wise) ── */}
      <section aria-label="screen-security">
        <h2>{t('roles.matrix.screenSecurity')}</h2>
        <p>{t('roles.matrix.screenSecurity.hint')}</p>
        <label htmlFor="resource-input">{t('roles.matrix.resource')}</label>
        <input
          id="resource-input"
          list="resource-options"
          value={resource}
          onChange={(e) => setResource(e.target.value.toUpperCase())}
        />
        <datalist id="resource-options">
          {RESOURCE_PRESETS.map((r) => (
            <option key={r} value={r} />
          ))}
        </datalist>

        <table aria-label="screen-security-matrix">
          <thead>
            <tr>
              <th scope="col">{t('roles.matrix.col.role')}</th>
              {SCREEN_ACTIONS.map((a) => (
                <th key={a.code} scope="col">
                  {t(a.labelKey)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {roles.map((role) => (
              <tr key={role.roleId}>
                <th scope="row">{role.roleName}</th>
                {SCREEN_ACTIONS.map((a) => {
                  const known = Boolean(permIdByCode[a.code]);
                  const checked = Boolean(grantFor(role.roleId, a.code));
                  return (
                    <td key={a.code}>
                      {known ? (
                        <GatedCheckbox
                          ariaLabel={`${role.roleCode}-${a.code}`}
                          checked={checked}
                          onToggle={(next) => toggleAction(role, a.code, next)}
                        />
                      ) : (
                        <span title={t('roles.matrix.permNotDefined')}>—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* ── §12 Field-level security ── */}
      <section aria-label="field-security">
        <h2>{t('roles.matrix.fieldSecurity')}</h2>
        <p>{t('roles.matrix.fieldSecurity.hint')}</p>

        <Can
          path="/admin/roles"
          action="edit"
          fallback={<p>{t('roles.matrix.fr.noPermission')}</p>}
        >
          <form aria-label="field-rule-form" onSubmit={addFieldRule} noValidate>
            {frError ? <p role="alert">{frError}</p> : null}
            <label htmlFor="fr-role">{t('roles.matrix.fr.role')}</label>
            <select id="fr-role" value={frRole} onChange={(e) => setFrRole(e.target.value)}>
              {roles.map((r) => (
                <option key={r.roleId} value={r.roleCode}>
                  {r.roleName}
                </option>
              ))}
            </select>

            <label htmlFor="fr-screen">{t('roles.matrix.fr.screen')}</label>
            <input id="fr-screen" value={frScreen} onChange={(e) => setFrScreen(e.target.value)} />

            <label htmlFor="fr-field">{t('roles.matrix.fr.field')}</label>
            <input id="fr-field" value={frField} onChange={(e) => setFrField(e.target.value)} />

            <label htmlFor="fr-visibility">{t('roles.matrix.fr.visibility')}</label>
            <select
              id="fr-visibility"
              value={frVis}
              onChange={(e) => setFrVis(e.target.value as FieldVisibility)}
            >
              {FIELD_VISIBILITIES.map((v) => (
                <option key={v} value={v}>
                  {FIELD_VISIBILITY_LABELS[v]}
                </option>
              ))}
            </select>

            <label htmlFor="fr-note">{t('roles.matrix.fr.note')}</label>
            <input id="fr-note" value={frNote} onChange={(e) => setFrNote(e.target.value)} />

            <button type="submit">{t('roles.matrix.fr.add')}</button>
          </form>
        </Can>

        <table aria-label="field-rules-table">
          <thead>
            <tr>
              <th scope="col">{t('roles.matrix.col.role')}</th>
              <th scope="col">{t('roles.matrix.col.screen')}</th>
              <th scope="col">{t('roles.matrix.col.field')}</th>
              <th scope="col">{t('roles.matrix.col.visibility')}</th>
              <th scope="col">{t('roles.matrix.col.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {fieldRules.length === 0 ? (
              <tr>
                <td colSpan={5}>{t('roles.matrix.fr.empty')}</td>
              </tr>
            ) : (
              fieldRules.map((rule) => (
                <tr key={rule.ruleId}>
                  <td>{rule.roleCode}</td>
                  <td>{rule.screenCode}</td>
                  <td>{rule.fieldName}</td>
                  <td>{FIELD_VISIBILITY_LABELS[rule.visibility] ?? rule.visibility}</td>
                  <td>
                    <Can path="/admin/roles" action="edit" fallback={<span>—</span>}>
                      <button
                        type="button"
                        aria-label={`deactivate-${rule.ruleId}`}
                        onClick={() => removeFieldRule(rule)}
                      >
                        {t('roles.matrix.fr.deactivate')}
                      </button>
                    </Can>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </section>
    </main>
  );
}
